import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Tabs,
  Tab,
  Box,
  Badge,
  CircularProgress,
  Drawer,
  useMediaQuery,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  Avatar,
  Tooltip,
  Typography,
  Skeleton,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import PropTypes from "prop-types";
// import Message from './Message'
import { API_URL, createAxiosInstance } from "../../../../api";
import {
  getMyMessages,
  seenMessages,
  setSelectedMessageId,
} from "../../../../store/setProfile";
import SmsFailedOutlinedIcon from "@mui/icons-material/SmsFailedOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonIcon from "@mui/icons-material/Person";
import "./Messages.css";
import ChatUI from "./Chat";
import { socket } from "../../../../socket";
import { DeleteOutline } from "@mui/icons-material";
import WarningDialog from "../../../../Components/Dialog/Warning";
import { toast } from "react-toastify";
import config from "config";
import { getAvatarUrl } from '../../../../utils/imageUtils';

function TabPanel(props) {
  const { children, value, index, sx, ...other } = props;

  if (value !== index) {
    return null;
  }

  return (
    <div
      role="tabpanel"
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      <Box sx={{ height: '100%', width: '100%', display: 'flex', ...sx }}>
        {children}
      </Box>
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]).isRequired,
  sx: PropTypes.object,
};

const Messages = () => {
  const drawerWidth = 240;
  const dispatch = useDispatch();
  const { messages } = useSelector((state) => state.profile);
  const [initialLoading, setInitialLoading] = React.useState(true);
  const [selectedUser, setSelectedUser] = React.useState({});
  const [value, setValue] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const smallScreen = useMediaQuery("(max-width: 899px)");
  const mobileScreen = useMediaQuery("(max-width: 600px)");
  const [drawerOpen, setDrawerOpen] = React.useState(!smallScreen);
  const selectedMessageId = React.useRef(null);
  const [openWarningDialog, setOpenWarningDialog] = React.useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  let senderId;
  try {
    const persistedData = localStorage.getItem("persist:root");
    const userData = JSON.parse(persistedData);
    if (userData.userId && typeof userData.userId === "string") {
      userData.userId = userData.userId.replace(/\\/g, "");
    }
    senderId = userData?.userId;
  } catch (error) {
    console.error("Error parsing localStorage data:", error);
    senderId = null;
  }

  senderId = senderId.replace(/"/g, ""); // Remove extra quotes;;
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleMessageIconClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseIconMenu = () => {
    setAnchorEl(null);
  };

  const handleCloseWarningDialog = () => {
    setOpenWarningDialog(false);
  };
  const handleOpenWarningDialog = () => {
    handleCloseIconMenu();
    setOpenWarningDialog(true);
  };

  useEffect(() => {
    if (socket) {
      socket.on("message", handleSocket);
      
      // Cleanup: remove event listener on unmount
      return () => {
        socket.off("message", handleSocket);
      };
    }
  }, []);

  const handleSocket = (message) => {
    // Refresh messages list to update last message preview when any message is received
    dispatch(getMyMessages());
    
    if (message?.messageId && message.messageId === selectedMessageId.current) {
      createAxiosInstance()
        .get(`/api/messages/${selectedMessageId.current}`)
        .then((res) => {
          dispatch(seenMessages(message.messageId));
          setSelectedUser(res.data);
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {});
    }
  };

  const handleChange = (event, newValue) => {
    if (!messages || !messages[newValue] || !messages[newValue]._id) {
      console.error("Invalid message selection");
      return;
    }
    setLoading(true);
    setValue(newValue);
    selectedMessageId.current = messages[newValue]._id;
    
    // Close drawer on mobile when a chat is selected
    if (smallScreen) {
      setDrawerOpen(false);
    }
    
    createAxiosInstance()
      .get(`/api/messages/${messages[newValue]._id}`)
      .then((res) => {
        dispatch(seenMessages(messages[newValue]._id));
        setSelectedUser(res.data);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  
  const handleBackToChatList = () => {
    // Open drawer but keep the current chat selected (don't reset value)
    setDrawerOpen(true);
    // Don't reset value - keep the active chat so it stays highlighted in the drawer
  };

  const sendMessageHandler = (
    userId,
    content,
    setMessageContent,
    setSending
  ) => {
    setSending(true);
    createAxiosInstance()
      .post(`/api/messages`, {
        content: content,
        receiverId: userId.toString(),
        senderId: senderId,
      })
      .then(({ data }) => {
        setSelectedUser(data);
        setMessageContent("");
        // Refresh messages list to update the last message preview in sidebar
        dispatch(getMyMessages());
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setSending(false);
      });
  };

  useEffect(() => {
    if (
      messages.length &&
      !value &&
      value !== 0 &&
      !selectedMessageId.current
    ) {
      handleChange(null, 0);
    }
  }, [messages.length]);

  // Handle drawer state on screen size changes
  useEffect(() => {
    if (!smallScreen) {
      setDrawerOpen(true);
    }
    // On mobile, don't auto-close drawer when chat is selected
    // Let user manually close it with the X button
  }, [smallScreen]);

  useEffect(() => {
    dispatch(getMyMessages());
  }, []);

  useEffect(() => {
    // mark initial load complete when we receive first array (even empty)
    if (Array.isArray(messages)) {
      setInitialLoading(false);
    }
  }, [messages.length]);

  const handleDeleteConversation = () => {
    setIsDeleting(true);
    createAxiosInstance()
      .delete(`/api/messages/delete-conversation/${selectedMessageId.current}`)
      .then(() => {
        selectedMessageId.current = null;
        setValue(null);
        toast.success("Conversation deleted successfully!");
        handleCloseWarningDialog();
        dispatch(getMyMessages());
      })
      .catch((err) => {
        toast.error(err?.response?.data ?? "Something went wrong!");
        console.log(err);
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  return (
    <Box sx={{ px: { xs: 0, sm: 2 }, overflow: 'hidden', height: '100%' }}>
      {/* Header with menu button on mobile */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 }, gap: 1 }}>
        {smallScreen && (
          <IconButton
            onClick={() => setDrawerOpen(true)}
            sx={{
              color: '#712d94',
              mr: 0.5,
            }}
          >
            <MenuIcon />
          </IconButton>
        )}
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            letterSpacing: 0.2,
            fontSize: { xs: '1.5rem', sm: '2rem' },
          }}
        >
        Messages
      </Typography>
      </Box>
      {initialLoading ? (
        <>
          <Box className="chatbox-container">
            <Drawer
              open={drawerOpen}
              onClose={() => setDrawerOpen(false)}
              sx={{
                width: { xl: 340, md: 300, xs: "100%" },
                flexShrink: 0,
                "& .MuiDrawer-paper": {
                  overflowY: "auto",
                  overflowX: "visible",
                  width: { xl: 340, md: 300, xs: "100%" },
                  flexDirection: { xs: "row", md: "column" },
                  boxSizing: "border-box",
                  borderRadius: 0,
                  border: { xs: "none", md: "1px solid #e5e7eb" },
                  borderRight: { md: "none" },
                  boxShadow: { xs: 'none', md: '1px 0 4px rgba(0,0,0,0.05)' },
                  position: "relative",
                  height: "100%",
                  display: 'flex',
                  flexDirection: 'column',
                },
              }}
              variant={smallScreen ? "temporary" : "permanent"}
              ModalProps={{
                keepMounted: true,
              }}
            >
              {/* Drawer Header with Close Button on Mobile */}
              {smallScreen && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1.5,
                    borderBottom: '1px solid #e5e7eb',
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      fontSize: '1rem',
                      color: '#1e293b',
                    }}
                  >
                    Conversations
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setDrawerOpen(false)}
                    sx={{
                      color: '#64748b',
                      '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.04)',
                        color: '#1e293b',
                      },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
              <Tabs
                orientation="vertical"
                sx={{
                  width: '100%',
                  height: '100%',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  py: 1.5,
                  px: 0.5,
                  bgcolor: '#ffffff',
                  boxSizing: 'border-box',
                  '& .MuiTabs-flexContainer': {
                    width: '100%',
                    overflow: 'hidden',
                  },
                }}
              >
                {[...Array(6)].map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      minHeight: { xs: 64, sm: 72 },
                      borderRadius: 2,
                      mx: 1,
                      my: 0.25,
                      px: { xs: 1.25, sm: 1.5 },
                      py: { xs: 1, sm: 1.25 },
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: { xs: 1, sm: 1.25 },
                      backgroundColor: 'transparent',
                      width: 'calc(100% - 16px)',
                      boxSizing: 'border-box',
                    }}
                  >
                    <Skeleton 
                      variant="rectangular" 
                      width={40} 
                      height={40} 
                      sx={{ 
                        borderRadius: 2,
                        bgcolor: 'rgba(113,45,148,0.1)',
                        flexShrink: 0,
                        mt: 0.25,
                      }} 
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1, minWidth: 0, py: 0.25 }}>
                      <Skeleton 
                        variant="text" 
                        sx={{ 
                          height: { xs: 16, sm: 18 },
                          width: '60%',
                        }} 
                      />
                      <Skeleton 
                        variant="text" 
                        sx={{ 
                          height: { xs: 12, sm: 14 },
                          width: '80%',
                        }} 
                      />
                    </Box>
                  </Box>
                ))}
              </Tabs>
            </Drawer>
            <Box 
              className="chatbox w-100" 
              sx={{ 
                height: '100%', 
                display: { xs: value === null ? 'none' : 'flex', md: 'flex' },
                flexDirection: 'column', 
                overflow: 'hidden',
                minWidth: { xs: 0, md: 640 },
              }}
            >
              {/* Header Skeleton */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  width: '100%',
                  borderRadius: 0,
                  px: { xs: 1.5, sm: 2.5 },
                  py: { xs: 1.5, sm: 2 },
                  background: '#ffffff',
                  borderBottom: `1px solid #f3f4f6`,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
                  flexShrink: 0,
                }}
              >
                <Skeleton 
                  variant="rectangular" 
                  width={32} 
                  height={32} 
                  sx={{ 
                    borderRadius: 2,
                    bgcolor: 'rgba(113,45,148,0.1)',
                    flexShrink: 0,
                  }} 
                />
                <Skeleton 
                  variant="text" 
                  width={120} 
                  height={18}
                  sx={{ flex: 1 }}
                />
              </Box>
              
              {/* Chat Area Skeleton */}
              <Box
                sx={{
                  flexGrow: 1,
                  overflow: "auto",
                  p: 3,
                  background: '#fafafa',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  minHeight: 0,
                }}
              >
                {[...Array(4)].map((_, i) => (
                  <Box key={i} sx={{ display: 'flex', justifyContent: i % 2 === 0 ? 'flex-start' : 'flex-end', mb: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: i % 2 === 0 ? 'flex-start' : 'flex-end', maxWidth: i % 2 === 0 ? '45%' : '40%' }}>
                      <Skeleton 
                        variant="rectangular" 
                        width={i % 2 === 0 ? 140 : 120}
                        height={i === 0 || i === 3 ? 45 : 60}
                        sx={{ 
                          borderRadius: i % 2 === 0 ? '16px 16px 16px 4px' : '16px 16px 4px 16px',
                          bgcolor: i % 2 === 0 ? '#f9fafb' : 'rgba(113,45,148,0.08)',
                        }} 
                      />
                      <Skeleton 
                        variant="text" 
                        width={35}
                        height={14}
                        sx={{ 
                          mt: 0.5,
                          alignSelf: i % 2 === 0 ? 'flex-start' : 'flex-end',
                        }} 
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
              
              {/* Input Skeleton */}
              <Box sx={{ p: { xs: 1, md: 1.5 }, backgroundColor: 'background.default', borderRadius: 0, flexShrink: 0 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 2,
                    py: 1,
                    border: '1px solid #e5e7eb',
                    borderRadius: 0,
                    backgroundColor: '#ffffff',
                  }}
                >
                  <Skeleton variant="circular" width={24} height={24} />
                  <Skeleton variant="text" sx={{ flex: 1, height: 20 }} />
                  <Skeleton variant="circular" width={24} height={24} />
                </Box>
              </Box>
            </Box>
          </Box>
        </>
      ) : messages.length ? (
        <>
          <Box className="chatbox-container">
            <Drawer
              open={drawerOpen}
              onClose={() => setDrawerOpen(false)}
              sx={{
                width: { xl: 340, md: 300, xs: "100%" },
                flexShrink: 0,
                "& .MuiDrawer-paper": {
                  overflowY: "auto",
                  overflowX: "visible",
                  width: { xl: 340, md: 300, xs: "100%" },
                  flexDirection: { xs: "row", md: "column" },
                  boxSizing: "border-box",
                  borderRadius: 0,
                  border: { xs: "none", md: "1px solid #e5e7eb" },
                  borderRight: { md: "none" },
                  boxShadow: { xs: 'none', md: '1px 0 4px rgba(0,0,0,0.05)' },
                  position: "relative",
                  height: "100%",
                  display: 'flex',
                  flexDirection: 'column',
                },
              }}
              variant={smallScreen ? "temporary" : "permanent"}
              ModalProps={{
                keepMounted: true,
              }}
            >
              {/* Drawer Header with Close Button on Mobile */}
              {smallScreen && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1.5,
                    borderBottom: '1px solid #e5e7eb',
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      fontSize: '1rem',
                      color: '#1e293b',
                    }}
                  >
                    Conversations
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setDrawerOpen(false)}
                    sx={{
                      color: '#64748b',
                      '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.04)',
                        color: '#1e293b',
                      },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
              <Tabs
                textColor="primary"
                indicatorColor="primary"
                orientation={!smallScreen ? "vertical" : "horizontal"}
                value={value !== null ? value : false}
                onChange={handleChange}
                variant="scrollable"
                scrollButtons={false}
                aria-label="Vertical tabs example"
                sx={{
                  width: '100%',
                  height: '100%',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  py: 1.5,
                  px: 0.5,
                  bgcolor: '#ffffff',
                  boxSizing: 'border-box',
                  '& .MuiTabs-flexContainer': {
                    width: '100%',
                    overflow: 'hidden',
                  },
                  '& .MuiTabs-scrollButtons': {
                    display: 'none',
                  },
                  '& .MuiTab-root': {
                    minHeight: { xs: 64, sm: 72 },
                    borderRadius: 2,
                    mx: 1,
                    my: 0.25,
                    textTransform: 'none',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    px: { xs: 1.25, sm: 1.5 },
                    py: { xs: 1, sm: 1.25 },
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    color: '#64748b',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    backgroundColor: 'transparent',
                    width: 'calc(100% - 16px)',
                    maxWidth: 'calc(100% - 16px)',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    '& .MuiTab-wrapper': {
                      width: '100%',
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'flex-start',
                      overflow: 'hidden',
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(113,45,148,0.04)',
                      color: '#1e293b',
                      transform: 'translateX(2px)',
                    }
                  },
                  "& .MuiTab-root.Mui-selected": {
                    color: '#fff',
                    fontWeight: 600,
                    backgroundColor: config["primary-colour-mui"],
                    boxShadow: '0 1px 3px rgba(113,45,148,0.12), 0 1px 2px rgba(113,45,148,0.24)',
                    transform: 'translateX(0)',
                    '&:hover': {
                      backgroundColor: config["primary-colour-mui-dark"] || config["primary-colour-mui"],
                      boxShadow: '0 2px 4px rgba(113,45,148,0.16), 0 1px 3px rgba(113,45,148,0.28)',
                      transform: 'translateX(0)',
                    }
                  },
                  "& .MuiTabs-indicator": {
                    display: 'none',
                  },
                }}
              >
                {messages.length ? (
                  messages.map((msg, index) => (
                      <Tab
                        key={msg._id}
                        label={
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', width: '100%', gap: { xs: 1, sm: 1.25 }, minWidth: 0, overflow: 'hidden', py: 0.25 }}>
                          <Box sx={{ position: 'relative', flexShrink: 0, mt: 0.25 }}>
                            {getAvatarUrl(msg.user.avatar, msg.user.gender) && 
                            !getAvatarUrl(msg.user.avatar, msg.user.gender).includes('User-avatar') &&
                            !getAvatarUrl(msg.user.avatar, msg.user.gender).includes('/male.png') &&
                            !getAvatarUrl(msg.user.avatar, msg.user.gender).includes('/female.png') ? (
                            <Avatar
                              sx={{
                                  width: { xs: 40, sm: 44 },
                                  height: { xs: 40, sm: 44 },
                                  bgcolor: value === index 
                                    ? 'rgba(255,255,255,0.25)' 
                                    : 'rgba(113,45,148,0.1)',
                                  borderRadius: 2,
                                  border: 'none',
                                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                  boxShadow: value === index 
                                    ? '0 1px 2px rgba(0,0,0,0.08)'
                                    : 'none',
                                  '& img': {
                                    objectFit: 'cover',
                                  },
                                }}
                                alt={`${msg.user.firstName} ${msg.user.lastName}`}
                              src={getAvatarUrl(msg.user.avatar, msg.user.gender)}
                                variant="rounded"
                                imgProps={{
                                  onError: (e) => {
                                    e.target.style.display = 'none';
                                  }
                                }}
                              >
                                <PersonIcon sx={{ fontSize: { xs: 20, sm: 22 }, color: value === index ? 'rgba(255,255,255,0.7)' : '#712d94' }} />
                              </Avatar>
                            ) : (
                              <Avatar
                                sx={{
                                  width: { xs: 40, sm: 44 },
                                  height: { xs: 40, sm: 44 },
                                  bgcolor: value === index 
                                    ? 'rgba(255,255,255,0.25)' 
                                    : 'rgba(113,45,148,0.1)',
                                  borderRadius: 2,
                                  border: 'none',
                                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                  boxShadow: value === index 
                                    ? '0 1px 2px rgba(0,0,0,0.08)'
                                    : 'none',
                                }}
                                variant="rounded"
                              >
                                <PersonIcon sx={{ fontSize: { xs: 20, sm: 22 }, color: value === index ? 'rgba(255,255,255,0.7)' : '#712d94' }} />
                              </Avatar>
                            )}
                            {!msg.isRead && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: -2,
                                  right: -2,
                                  width: { xs: 10, sm: 12 },
                                  height: { xs: 10, sm: 12 },
                                  bgcolor: value === index ? 'rgba(255,255,255,0.9)' : '#ef4444',
                                  borderRadius: '50%',
                                  border: value === index 
                                    ? '2px solid rgba(113,45,148,0.3)'
                                    : '2px solid #fff',
                                  boxShadow: value === index 
                                    ? 'none'
                                    : '0 1px 2px rgba(239, 68, 68, 0.2)',
                                }}
                              />
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, overflow: 'hidden', gap: 0.25 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                              <Typography
                                sx={{
                                  fontWeight: value === index ? 600 : (!msg.isRead ? 600 : 500),
                                  fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                                  maxWidth: '100%',
                                  overflow: 'hidden',
                                  whiteSpace: 'nowrap',
                                  textOverflow: 'ellipsis',
                                  color: value === index ? '#fff' : '#1e293b',
                                  transition: 'all 0.2s ease',
                                  lineHeight: 1.4,
                                  letterSpacing: '-0.01em',
                                  flex: 1,
                                  minWidth: 0,
                                }}
                              >
                                {msg.user.firstName} {msg.user.lastName}
                              </Typography>
                              {value === index && (
                                <MoreVertIcon
                                  fontSize="small"
                                  sx={{
                                    color: 'rgba(255,255,255,0.85)',
                                    transition: 'all 0.2s ease',
                                    cursor: 'pointer',
                                    opacity: 0.7,
                                    flexShrink: 0,
                                    '&:hover': {
                                      opacity: 1,
                                      color: '#fff',
                                      transform: 'scale(1.05)',
                                    }
                                  }}
                                  aria-controls={open ? "basic-menu" : undefined}
                                  aria-haspopup="true"
                                  aria-expanded={open ? "true" : undefined}
                                  onClick={handleMessageIconClick}
                                />
                              )}
                            </Box>
                            {msg.content && (
                              <Typography
                              sx={{
                                  fontWeight: value === index ? 400 : (!msg.isRead ? 500 : 400),
                                  fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                                  maxWidth: '100%',
                                  overflow: 'hidden',
                                  whiteSpace: 'nowrap',
                                  textOverflow: 'ellipsis',
                                  color: value === index ? 'rgba(255,255,255,0.8)' : (!msg.isRead ? '#334155' : '#64748b'),
                                  transition: 'all 0.2s ease',
                                  lineHeight: 1.4,
                                  letterSpacing: '-0.005em',
                                }}
                              >
                                {msg.content}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      }
                    />
                  ))
                ) : (
                  <Box sx={{ p: 2, color: '#6b7280', width: '100%' }}>
                    <Typography variant="body2" sx={{ px: 2, opacity: 0.8 }}>
                      No conversations yet
                    </Typography>
                  </Box>
                )}
              </Tabs>
            </Drawer>
            {messages.map((msg, index) => (
              <TabPanel
                key={msg._id}
                value={value !== null ? value : false}
                index={index}
                sx={{
                  display: { xs: value === index ? 'flex' : 'none', md: 'flex' },
                }}
                className="tab-panel-container w-100"
              >
                {loading ? (
                  <div
                    className={"message-box"}
                    style={{ textAlign: "center", padding: "100px" }}
                  >
                    <CircularProgress />
                  </div>
                ) : (
                  <ChatUI
                    sender={msg.user}
                    message={selectedUser}
                    onSendMessage={sendMessageHandler}
                    onBack={smallScreen ? handleBackToChatList : undefined}
                  />
                )}
              </TabPanel>
            ))}
          </Box>
        </>
      ) : messages.length === 0 ? (
        <>
          <Box className="chatbox-container">
            {!smallScreen && (
              <Drawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                sx={{
                  width: { xl: 340, md: 300 },
                  flexShrink: 0,
                  "& .MuiDrawer-paper": {
                    overflowY: "auto",
                    overflowX: "visible",
                    width: { xl: 340, md: 300 },
                    flexDirection: "column",
                    boxSizing: "border-box",
                    borderRadius: 0,
                    border: "1px solid #e5e7eb",
                    borderRight: "none",
                    boxShadow: '1px 0 4px rgba(0,0,0,0.05)',
                    position: "relative",
                    height: "100%",
                    display: 'flex',
                    flexDirection: 'column',
                  },
                }}
                variant="permanent"
              >
                <Box sx={{ p: 2, color: '#6b7280', width: '100%' }}>
                  <Typography variant="body2" sx={{ px: 2, opacity: 0.8 }}>
                    No conversations yet
                  </Typography>
                </Box>
              </Drawer>
            )}
            <div className="empty-chat-panel">
              <div className="empty-chat-content">
                <SmsFailedOutlinedIcon fontSize={"large"} style={{ color: '#9ca3af' }} />
                <div className="empty-chat-title">No active chats</div>
                <div className="empty-chat-subtitle">Start a conversation from a tutor profile.</div>
              </div>
          </div>
          </Box>
        </>
      ) : (
        <>
          <Box className="chatbox-container">
            <Drawer
              open={drawerOpen}
              onClose={() => {
                // Only close drawer, don't reset the selected chat
                setDrawerOpen(false);
              }}
              sx={{
                width: { xl: 340, md: 300, xs: "100%" },
                flexShrink: 0,
                "& .MuiDrawer-paper": {
                  overflowY: "auto",
                  overflowX: "visible",
                  width: { xl: 340, md: 300, xs: "100%" },
                  flexDirection: { xs: "row", md: "column" },
                  boxSizing: "border-box",
                  borderRadius: 0,
                  border: { xs: "none", md: "1px solid #e5e7eb" },
                  borderRight: { md: "none" },
                  boxShadow: { xs: 'none', md: '1px 0 4px rgba(0,0,0,0.05)' },
                  position: "relative",
                  height: "100%",
                  display: 'flex',
                  flexDirection: 'column',
                },
              }}
              variant={smallScreen ? "temporary" : "permanent"}
              ModalProps={{
                keepMounted: true,
              }}
            >
              {/* Drawer Header with Close Button on Mobile */}
              {smallScreen && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1.5,
                    borderBottom: '1px solid #e5e7eb',
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      fontSize: '1rem',
                      color: '#1e293b',
                    }}
                  >
                    Conversations
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setDrawerOpen(false)}
                    sx={{
                      color: '#64748b',
                      '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.04)',
                        color: '#1e293b',
                      },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
              <Tabs
                textColor="primary"
                indicatorColor="primary"
                orientation={!smallScreen ? "vertical" : "horizontal"}
                value={value !== null ? value : false}
                onChange={handleChange}
                variant="scrollable"
                scrollButtons={false}
                aria-label="Vertical tabs example"
                sx={{
                  width: '100%',
                  height: '100%',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  py: 1.5,
                  px: 0.5,
                  bgcolor: '#ffffff',
                  boxSizing: 'border-box',
                  '& .MuiTabs-flexContainer': {
                    width: '100%',
                    overflow: 'hidden',
                  },
                  '& .MuiTabs-scrollButtons': {
                    display: 'none',
                  },
                  '& .MuiTab-root': {
                    minHeight: { xs: 64, sm: 72 },
                    borderRadius: 2,
                    mx: 1,
                    my: 0.25,
                    textTransform: 'none',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    px: { xs: 1.25, sm: 1.5 },
                    py: { xs: 1, sm: 1.25 },
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    color: '#64748b',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    backgroundColor: 'transparent',
                    width: 'calc(100% - 16px)',
                    maxWidth: 'calc(100% - 16px)',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    '& .MuiTab-wrapper': {
                      width: '100%',
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'flex-start',
                      overflow: 'hidden',
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(113,45,148,0.04)',
                      color: '#1e293b',
                      transform: 'translateX(2px)',
                    }
                  },
                  "& .MuiTab-root.Mui-selected": {
                    color: '#fff',
                    fontWeight: 600,
                    backgroundColor: config["primary-colour-mui"],
                    boxShadow: '0 1px 3px rgba(113,45,148,0.12), 0 1px 2px rgba(113,45,148,0.24)',
                    transform: 'translateX(0)',
                    '&:hover': {
                      backgroundColor: config["primary-colour-mui-dark"] || config["primary-colour-mui"],
                      boxShadow: '0 2px 4px rgba(113,45,148,0.16), 0 1px 3px rgba(113,45,148,0.28)',
                      transform: 'translateX(0)',
                    }
                  },
                  "& .MuiTabs-indicator": {
                    display: 'none',
                  },
                }}
              >
                {messages.length ? (
                  messages.map((msg, index) => (
                      <Tab
                        key={msg._id}
                        label={
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', width: '100%', gap: { xs: 1, sm: 1.25 }, minWidth: 0, overflow: 'hidden', py: 0.25 }}>
                          <Box sx={{ position: 'relative', flexShrink: 0, mt: 0.25 }}>
                            {getAvatarUrl(msg.user.avatar, msg.user.gender) && 
                            !getAvatarUrl(msg.user.avatar, msg.user.gender).includes('User-avatar') &&
                            !getAvatarUrl(msg.user.avatar, msg.user.gender).includes('/male.png') &&
                            !getAvatarUrl(msg.user.avatar, msg.user.gender).includes('/female.png') ? (
                          <Avatar
                            sx={{
                                  width: { xs: 40, sm: 44 },
                                  height: { xs: 40, sm: 44 },
                                  bgcolor: value === index 
                                    ? 'rgba(255,255,255,0.25)' 
                                    : 'rgba(113,45,148,0.1)',
                                  borderRadius: 2,
                                  border: 'none',
                                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                  boxShadow: value === index 
                                    ? '0 1px 2px rgba(0,0,0,0.08)'
                                    : 'none',
                                  '& img': {
                                    objectFit: 'cover',
                                  },
                                }}
                                alt={`${msg.user.firstName} ${msg.user.lastName}`}
                            src={getAvatarUrl(msg.user.avatar, msg.user.gender)}
                                variant="rounded"
                                imgProps={{
                                  onError: (e) => {
                                    e.target.style.display = 'none';
                                  }
                                }}
                              >
                                <PersonIcon sx={{ fontSize: { xs: 20, sm: 22 }, color: value === index ? 'rgba(255,255,255,0.7)' : '#712d94' }} />
                              </Avatar>
                            ) : (
                              <Avatar
                                sx={{
                                  width: { xs: 40, sm: 44 },
                                  height: { xs: 40, sm: 44 },
                                  bgcolor: value === index 
                                    ? 'rgba(255,255,255,0.25)' 
                                    : 'rgba(113,45,148,0.1)',
                                  borderRadius: 2,
                                  border: 'none',
                                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                  boxShadow: value === index 
                                    ? '0 1px 2px rgba(0,0,0,0.08)'
                                    : 'none',
                                }}
                                variant="rounded"
                              >
                                <PersonIcon sx={{ fontSize: { xs: 20, sm: 22 }, color: value === index ? 'rgba(255,255,255,0.7)' : '#712d94' }} />
                              </Avatar>
                            )}
                            {!msg.isRead && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: -2,
                                  right: -2,
                                  width: { xs: 10, sm: 12 },
                                  height: { xs: 10, sm: 12 },
                                  bgcolor: value === index ? 'rgba(255,255,255,0.9)' : '#ef4444',
                                  borderRadius: '50%',
                                  border: value === index 
                                    ? '2px solid rgba(113,45,148,0.3)'
                                    : '2px solid #fff',
                                  boxShadow: value === index 
                                    ? 'none'
                                    : '0 1px 2px rgba(239, 68, 68, 0.2)',
                                }}
                              />
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, overflow: 'hidden', gap: 0.25 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                              <Typography
                                sx={{
                                  fontWeight: value === index ? 600 : (!msg.isRead ? 600 : 500),
                                  fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                                  maxWidth: '100%',
                                  overflow: 'hidden',
                                  whiteSpace: 'nowrap',
                                  textOverflow: 'ellipsis',
                                  color: value === index ? '#fff' : '#1e293b',
                                  transition: 'all 0.2s ease',
                                  lineHeight: 1.4,
                                  letterSpacing: '-0.01em',
                                  flex: 1,
                                  minWidth: 0,
                                }}
                              >
                                {msg.user.firstName} {msg.user.lastName}
                              </Typography>
                              {value === index && (
                                <MoreVertIcon
                                  fontSize="small"
                                  sx={{
                                    color: 'rgba(255,255,255,0.85)',
                                    transition: 'all 0.2s ease',
                                    cursor: 'pointer',
                                    opacity: 0.7,
                                    flexShrink: 0,
                                    '&:hover': {
                                      opacity: 1,
                                      color: '#fff',
                                      transform: 'scale(1.05)',
                                    }
                                  }}
                                  aria-controls={open ? "basic-menu" : undefined}
                                  aria-haspopup="true"
                                  aria-expanded={open ? "true" : undefined}
                                  onClick={handleMessageIconClick}
                                />
                              )}
                            </Box>
                            {msg.content && (
                              <Typography
                                sx={{
                                  fontWeight: value === index ? 400 : (!msg.isRead ? 500 : 400),
                                  fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                                  maxWidth: '100%',
                                  overflow: 'hidden',
                                  whiteSpace: 'nowrap',
                                  textOverflow: 'ellipsis',
                                  color: value === index ? 'rgba(255,255,255,0.8)' : (!msg.isRead ? '#334155' : '#64748b'),
                                  transition: 'all 0.2s ease',
                                  lineHeight: 1.4,
                                  letterSpacing: '-0.005em',
                                }}
                              >
                                {msg.content}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      }
                    />
                  ))
                ) : (
                  <Box sx={{ p: 2, color: '#6b7280', width: '100%' }}>
                    <Typography variant="body2" sx={{ px: 2, opacity: 0.8 }}>
                      No conversations yet
                    </Typography>
                  </Box>
                )}
              </Tabs>
            </Drawer>
            {messages.map((msg, index) => (
              <TabPanel
                key={msg._id}
                value={value !== null ? value : false}
                index={index}
                sx={{
                  display: { xs: value === index ? 'flex' : 'none', md: 'flex' },
                }}
                className="tab-panel-container w-100"
              >
                {loading ? (
                  <div
                    className={"message-box"}
                    style={{ textAlign: "center", padding: "100px" }}
                  >
                    <CircularProgress />
                  </div>
                ) : (
                  <ChatUI
                    sender={msg.user}
                    message={selectedUser}
                    onSendMessage={sendMessageHandler}
                    onBack={smallScreen ? handleBackToChatList : undefined}
                  />
                )}
              </TabPanel>
            ))}
            {/* Empty state when no chat is selected on mobile - only show when drawer is closed */}
            {smallScreen && value === null && !drawerOpen && (
              <Box
                className="empty-chat-panel"
                sx={{
                  flex: 1,
                  display: { xs: 'flex', md: 'none' },
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box className="empty-chat-content">
                  <SmsFailedOutlinedIcon fontSize={"large"} style={{ color: '#9ca3af' }} />
                  <div className="empty-chat-title">No chat selected</div>
                  <div className="empty-chat-subtitle">Select a conversation to start chatting.</div>
                </Box>
              </Box>
            )}
            {/* Show active chat even when drawer is open on mobile (chat appears behind drawer) */}
            {/* This ensures when drawer closes, the chat is already visible */}
          </Box>
        </>
      )}
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseIconMenu}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        <Tooltip
          title="Delete"
          placement="right-start"
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [6, -5],
                  },
                },
              ],
            },
          }}
          arrow
        >
          <MenuItem onClick={handleOpenWarningDialog}>
            <ListItemIcon sx={{ justifyContent: "center" }}>
              <DeleteOutline fontSize="small" color="error" />
            </ListItemIcon>
          </MenuItem>
        </Tooltip>
      </Menu>
      <WarningDialog
        open={openWarningDialog}
        handleSubmit={handleDeleteConversation}
        isLoading={isDeleting}
        handleClose={handleCloseWarningDialog}
        title="Delete Conversation"
        content="This action won't be reversable. Are you sure you want to delete this conversation?"
      />
    </Box>
  );
};

export default Messages;
